/**
 * Comparator Floating Bar - Phase 3/3
 * Fixed bar at bottom showing selected products
 */
import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useComparator } from '../context/ComparatorContext';
import { Button } from '@/components/ui/button';
import { FaExchangeAlt, FaTimes, FaArrowRight, FaTrash } from 'react-icons/fa';

export default function ComparatorFloatingBar() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const { 
    comparatorData, 
    showFloatingBar, 
    removeProduct, 
    clearComparator,
    maxProducts 
  } = useComparator();

  // Don't show on comparator page itself
  const isComparatorPage = location.pathname === '/comparateur';
  const shouldShow = showFloatingBar && comparatorData.products.length > 0 && !isComparatorPage;

  // Add padding to body when floating bar is visible
  useEffect(() => {
    if (shouldShow) {
      document.body.style.paddingBottom = '80px';
    } else {
      document.body.style.paddingBottom = '0';
    }
    return () => {
      document.body.style.paddingBottom = '0';
    };
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9999]"
      data-testid="comparator-floating-bar"
      style={{
        animation: 'slideUp 0.3s ease-out'
      }}
    >
      {/* Main bar */}
      <div className="bg-gradient-to-r from-[#0A2540] to-[#1a365d] text-white shadow-2xl border-t-2 border-[#635BFF]">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Icon & Info */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#635BFF] rounded-full flex items-center justify-center animate-pulse">
                <FaExchangeAlt className="text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-lg">
                    {comparatorData.products.length}/{maxProducts}
                  </span>
                  <span className="text-white/80 text-sm">
                    {i18n.language === 'fr' ? 'produit(s)' : 'product(s)'}
                  </span>
                  <span className="bg-[#635BFF]/30 px-2 py-0.5 rounded text-xs hidden sm:inline">
                    {comparatorData.category_name}
                  </span>
                </div>
                <p className="text-xs text-white/60 hidden md:block">
                  {comparatorData.products.length < 2 
                    ? (i18n.language === 'fr' 
                        ? 'Ajoutez un autre produit de la même catégorie' 
                        : 'Add another product from the same category')
                    : (i18n.language === 'fr' 
                        ? 'Prêt à comparer !' 
                        : 'Ready to compare!')
                  }
                </p>
              </div>
            </div>

            {/* Center: Product thumbnails */}
            <div className="hidden md:flex items-center gap-3 flex-1 justify-center">
              {comparatorData.products.map((product) => (
                <div 
                  key={product.id}
                  className="relative group"
                >
                  <div className="w-14 h-14 rounded-lg bg-white/10 border-2 border-white/30 overflow-hidden flex items-center justify-center shadow-lg">
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl">📦</span>
                    )}
                  </div>
                  {/* Remove button on hover */}
                  <button
                    onClick={() => removeProduct(product.id)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg hover:scale-110"
                    title={i18n.language === 'fr' ? 'Retirer' : 'Remove'}
                  >
                    <FaTimes className="text-white text-xs" />
                  </button>
                  {/* Product name tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-black/90 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
                    {product.name}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/90"></div>
                  </div>
                </div>
              ))}
              
              {/* Empty slots */}
              {Array.from({ length: maxProducts - comparatorData.products.length }).map((_, i) => (
                <div 
                  key={`empty-${i}`}
                  className="w-14 h-14 rounded-lg border-2 border-dashed border-white/30 flex items-center justify-center"
                >
                  <span className="text-white/40 text-xl">+</span>
                </div>
              ))}
            </div>

            {/* Mobile: Compact product count */}
            <div className="flex md:hidden items-center gap-1">
              {comparatorData.products.slice(0, 2).map((product) => (
                <div 
                  key={product.id}
                  className="w-8 h-8 rounded bg-white/10 border border-white/20 overflow-hidden"
                >
                  {product.image_url ? (
                    <img src={product.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm flex items-center justify-center h-full">📦</span>
                  )}
                </div>
              ))}
              {comparatorData.products.length > 2 && (
                <span className="text-white/70 text-xs">+{comparatorData.products.length - 2}</span>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearComparator}
                className="text-white/70 hover:text-white hover:bg-white/10"
                title={i18n.language === 'fr' ? 'Vider le comparateur' : 'Clear comparator'}
              >
                <FaTrash className="mr-1" />
                <span className="hidden sm:inline">
                  {i18n.language === 'fr' ? 'Vider' : 'Clear'}
                </span>
              </Button>
              
              <Link to="/comparateur">
                <Button 
                  className={`text-white font-semibold ${
                    comparatorData.products.length >= 2 
                      ? 'bg-[#635BFF] hover:bg-[#5248d9]' 
                      : 'bg-gray-500 cursor-not-allowed'
                  }`}
                  disabled={comparatorData.products.length < 2}
                >
                  <FaExchangeAlt className="mr-2" />
                  {i18n.language === 'fr' ? 'Comparer' : 'Compare'}
                  <FaArrowRight className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
